import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge, Box, Button, CircularProgress, Divider, IconButton, ListItemButton,
  Popover, Stack, Tooltip, Typography,
} from "@mui/material";
import {
  NotificationsNone, PersonAddAlt1, ReceiptLong, Verified, DoneAll,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { AdminNotification, AdminNotificationType, notificationsAPI } from "../../services/adminApi";

const BLUE  = "#1565C0";
const NAVY  = "#0A1628";
const MUTED = "#78909C";

/** How often the badge asks the server for the unread count, in ms. */
const POLL_INTERVAL_MS = 60_000;
/** How many notifications the dropdown shows — the rest live in the list page. */
const DROPDOWN_LIMIT = 20;

const TYPE_META: Record<AdminNotificationType, { icon: React.ReactNode; color: string }> = {
  "user.registered":        { icon: <PersonAddAlt1 sx={{ fontSize: 18 }} />, color: "#7C3AED" },
  "subscription.requested": { icon: <ReceiptLong sx={{ fontSize: 18 }} />,   color: "#D97706" },
  "subscription.activated": { icon: <Verified sx={{ fontSize: 18 }} />,      color: "#059669" },
};

const relativeTime = (iso: string) => {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ""; }
};

/**
 * Admin-panel bell: unread badge polled in the background, dropdown with the
 * latest notifications, click-through to the page that needs attention.
 */
const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(false);
  // Guards against a slow response landing after the component unmounted.
  const mounted = useRef(true);

  const refreshCount = useCallback(async () => {
    try {
      const res = await notificationsAPI.unreadCount();
      if (mounted.current) setUnread(res.data.unreadCount);
    } catch {
      // Keep the last known badge — a blip in polling isn't worth surfacing.
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refreshCount();
    const timer = setInterval(() => {
      // Don't poll a tab nobody is looking at; catch up when it comes back.
      if (document.visibilityState === "visible") refreshCount();
    }, POLL_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") refreshCount(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted.current = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshCount]);

  const open = async (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    setLoading(true);
    try {
      const res = await notificationsAPI.list({ limit: DROPDOWN_LIMIT });
      if (mounted.current) {
        setItems(res.data.data);
        setUnread(res.data.unreadCount);
      }
    } catch {
      if (mounted.current) setItems([]);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const close = () => setAnchor(null);

  const openItem = async (n: AdminNotification) => {
    close();
    if (!n.readAt) {
      // Optimistic: the dot disappears now, the server catches up.
      setItems(prev => prev.map(x => (x._id === n._id ? { ...x, readAt: new Date().toISOString() } : x)));
      setUnread(u => Math.max(0, u - 1));
      notificationsAPI.markRead(n._id).catch(() => refreshCount());
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    const now = new Date().toISOString();
    setItems(prev => prev.map(x => (x.readAt ? x : { ...x, readAt: now })));
    setUnread(0);
    try { await notificationsAPI.markAllRead(); } catch { refreshCount(); }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={open} aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
          sx={{ color: BLUE }}>
          <Badge badgeContent={unread} max={99} color="error"
            sx={{ "& .MuiBadge-badge": { fontWeight: 700, fontSize: ".65rem", minWidth: 18, height: 18 } }}>
            <NotificationsNone />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: {
          width: { xs: "calc(100vw - 24px)", sm: 400 }, maxWidth: 420, borderRadius: "16px",
          boxShadow: "0 16px 40px rgba(10,22,40,0.16)", border: "1px solid rgba(21,101,192,0.12)",
          overflow: "hidden",
        } } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontWeight: 800, color: NAVY }}>Notifications</Typography>
          <Button size="small" startIcon={<DoneAll sx={{ fontSize: "16px !important" }} />}
            onClick={markAll} disabled={unread === 0}
            sx={{ textTransform: "none", fontWeight: 600, color: BLUE }}>
            Mark all read
          </Button>
        </Stack>
        <Divider />

        <Box sx={{ maxHeight: 440, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : items.length === 0 ? (
            <Typography sx={{ color: MUTED, textAlign: "center", py: 4, fontSize: ".9rem" }}>
              You're all caught up.
            </Typography>
          ) : (
            items.map(n => {
              const meta = TYPE_META[n.type] ?? TYPE_META["subscription.activated"];
              const isUnread = !n.readAt;
              return (
                <ListItemButton key={n._id} onClick={() => openItem(n)}
                  sx={{
                    alignItems: "flex-start", gap: 1.5, px: 2, py: 1.5,
                    bgcolor: isUnread ? "rgba(21,101,192,0.05)" : "transparent",
                    borderBottom: "1px solid rgba(21,101,192,0.08)",
                    "&:hover": { bgcolor: "rgba(21,101,192,0.09)" },
                  }}>
                  <Box sx={{
                    width: 34, height: 34, borderRadius: "10px", flexShrink: 0, mt: .25,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: `${meta.color}1A`, color: meta.color,
                  }}>
                    {meta.icon}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontWeight: isUnread ? 800 : 600, color: NAVY, fontSize: ".88rem", flex: 1, minWidth: 0 }}
                        noWrap>
                        {n.title}
                      </Typography>
                      {isUnread && (
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BLUE, flexShrink: 0 }} />
                      )}
                    </Stack>
                    <Typography sx={{
                      color: "#37474F", fontSize: ".8rem", lineHeight: 1.45, mt: .25,
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                    }}>
                      {n.message}
                    </Typography>
                    <Typography sx={{ color: MUTED, fontSize: ".72rem", mt: .5 }}>
                      {relativeTime(n.createdAt)}
                    </Typography>
                  </Box>
                </ListItemButton>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
